"use client";

import { mapToSupportedLocale, useLocale } from "@i18n/react";
import { useConsent } from "@packages/consent";
import { useSearch } from "@packages/framework";
import {
  getDeviceIsMobile,
  getDeviceType,
  getFullName,
  useAccessibility,
  useRequestEvent,
  useRequestMe,
  uuid,
} from "@packages/sdk";
import { AnalyticsBrowser } from "@segment/analytics-next";
import type { ComponentProps } from "react";
import { Suspense, useCallback, useEffect, useState } from "react";

import { analyticsContext } from "../lib/analyticsContext";
import type { AnalyticsEvent } from "../types/analyticsEvent.types";
import { HallowAnalyticsHandler } from "../types/enums/hallowAnalyticsHandler.enum";
import { getScreenName } from "../utils/getScreenName";
import { getSearchAttributes } from "../utils/getSearchAttributes";
import { trackCrossPlatform } from "../utils/trackCrossPlatform";

let analytics: AnalyticsBrowser;
export const getAnalytics = (): AnalyticsBrowser => {
  if (!analytics) analytics = new AnalyticsBrowser();
  // analytics = new (
  //   await import("@segment/analytics-next")
  // ).AnalyticsBrowser();
  return analytics;
};

type AnalyticsProviderProps = Omit<
  ComponentProps<typeof analyticsContext.Provider>,
  "value"
> & {
  options?: {
    pathToScreenNameMap: Record<string, string>;
  };
};

const Provider = ({ children, options, ...props }: AnalyticsProviderProps) => {
  const search = useSearch();
  const isTrackCrossPlatform = search.get("isTrackCrossPlatform") === "true";

  const [isIdentified, setIsIdentified] = useState(false);

  const locale = useLocale();

  const { hlwFontStyle, hlwLetterSpacing, hlwWordSpacing, preferredTheme } =
    useAccessibility();
  const { consent, isConsenting, setIsError } = useConsent();
  const { query: queryMe } = useRequestMe();
  const { mutationPost: postEvent } = useRequestEvent();

  const logout = () => {
    const analytics = getAnalytics();
    return analytics.reset();
  };

  const track = ({
    event,
    handler = HallowAnalyticsHandler.Track,
    properties,
  }: AnalyticsEvent) => {
    const isWindowExists = typeof window !== "undefined";

    const trackProperties = {
      device_type: getDeviceType(),
      font_style: hlwFontStyle,
      is_anonymous: !isIdentified,
      letter_spacing: hlwLetterSpacing,
      locale,
      mobile: getDeviceIsMobile(),
      platform: "web",
      screen_height: isWindowExists ? window?.innerHeight : 0,

      screen_name: isWindowExists
        ? getScreenName(window?.location.pathname, {
            pathToScreenNameMap: options?.pathToScreenNameMap ?? {},
          })
        : "",
      screen_width: isWindowExists ? window?.innerWidth : 0,
      slug: isWindowExists
        ? `${window.location.pathname}${window.location.search}${window.location.hash}`
        : "",
      theme: preferredTheme,
      word_spacing: hlwWordSpacing,
      ...properties,
      ...getSearchAttributes({ search }),
      subscription_plan: queryMe.data?.subscription?.product ?? null,
      subscription_is_trial: queryMe.data?.subscription?.is_trial ?? null,
    };

    if (isTrackCrossPlatform)
      return trackCrossPlatform({
        event: { event, properties: trackProperties },
        handler,
      }) as unknown as Promise<void>;
    const analytics = getAnalytics();
    return analytics.track(event, trackProperties) as unknown as Promise<void>;
  };
  const backendEvent = useCallback(
    ({ event, body }) => {
      const eventUuid = uuid();
      const createdAt = new Date();
      return postEvent.mutateAsync({
        event,
        body,
        uuid: eventUuid,
        created_at: createdAt,
      });
    },
    [postEvent],
  );

  /** If the user is consenting, load analytics */
  useEffect(() => {
    if (isConsenting) {
      const analytics = getAnalytics();
      analytics
        .load(
          {
            writeKey: process.env.NEXT_PUBLIC_SEGMENT_WRITE_KEY,
          },
          {
            integrations: {
              "Segment.io": {
                apiHost: process.env.NEXT_PUBLIC_SEGMENT_API_HOST,
                protocol: "https",
              },
            },
          },
        )
        .catch(() => setIsError(true));
    }
  }, [isConsenting]);

  /**
   * If user authenticates, sync pre-auth and post-auth analytics
   * Segment analytics.identify() syncs events
   */
  const isIdentifiable = isConsenting && !isIdentified && queryMe.data?.id;
  useEffect(() => {
    if (isIdentifiable) {
      const analytics = getAnalytics();
      const userLocale =
        queryMe.data?.language ||
        (typeof window !== "undefined"
          ? mapToSupportedLocale(navigator.language)
          : locale);

      analytics.identify(queryMe.data.id.toString(), {
        consent,
        email: queryMe.data?.email,
        locale: userLocale,
        name: getFullName({
          firstName: queryMe.data?.name,
          lastName: queryMe.data?.last_name,
        }),
        phoneNumber: queryMe.data?.phone,
      });
      setIsIdentified(true);
    }
  }, [isIdentifiable]);

  return (
    <analyticsContext.Provider
      value={{
        logout,
        track,
        postEvent: backendEvent,
      }}
      {...props}
    >
      {children}
    </analyticsContext.Provider>
  );
};

export const AnalyticsProvider = ({
  children,
  ...props
}: AnalyticsProviderProps) => (
  <Suspense>
    <Provider {...props}>{children}</Provider>
  </Suspense>
);
